import * as THREE from 'three';

import { CORNERS, HEX_SIZE, elevationToY } from '../map/HexMetrics';
import { axialKey, axialToWorld, neighbor } from '../map/HexCoordinates';
import { HexCellData, cornerElevation } from '../map/HexSurface';
import { habitable, passable } from '../map/Movement';
import { Props } from './PropModels';

/**
 * Kamienie wzdłuż krawędzi, przez które nie da się przejść.
 *
 * **Próba, i tak jest opisana.** Klif jako pionowa ściana w tej grze nie
 * istnieje — duża różnica poziomów daje strome, ale gładkie zbocze, a to, czy
 * da się przejść, jest regułą na danych. Widać z tego dokładnie nic: gracz
 * odbija się od miejsca, które wygląda jak każde inne. Rząd głazów na takiej
 * krawędzi jest najtańszą próbą **pokazania tej reguły w świecie** zamiast
 * w nakładce debugowej — dlatego rysuje się zawsze, a nie pod klawiszem.
 *
 * Osobno od `PropField`, choć sięga do tych samych modeli: tamto stawia rzeczy
 * **na polu** i pyta o jego zasobność, to stawia je **na granicy dwóch pól**
 * i pyta o regułę ruchu. Wspólny byłby tylko wiersz z macierzą.
 *
 * **Jeden model na krawędź, a nie rząd losowych głazów** — i to jest zmiana
 * wobec pierwszej wersji. Tamta sypała cztery kamienie z trzech plików, każdy
 * z własnym obrotem, przechyleniem i wielkością: z daleka czytało się to jako
 * usypisko, a z bliska jako cztery osobne bryły, między którymi widać przejście,
 * którego nie ma. Model zrobiony **pod krawędź** — długi, niski, skierowany
 * wzdłuż niej — mówi to samo jednym kształtem i jedną instancją zamiast czterech.
 */

/** O ile przegroda jest wpuszczona w ziemię, jako ułamek jej wysokości. */
const SUNK = 0.2;

/**
 * O ile cały pas jest przesunięty **na stronę spadku**.
 *
 * Przegroda leżąca dokładnie na krawędzi wygląda jak płot postawiony na
 * granicy działki. Rząd zsunięty ku niższemu polu czyta się jak **krawędź
 * urwiska** — kamienie obsypane z góry, leżące na skraju tego, co niżej — a to
 * jest ta rzecz, którą reguła kroku naprawdę opisuje. Ułamek promienia heksa.
 */
const TOWARDS_DROP = HEX_SIZE * 0.16;

/**
 * Głazy na wszystkich nieprzekraczalnych krawędziach chunka, albo `null`, gdy
 * żadnej nie ma.
 *
 * Krawędź rysowana **raz**: należy do dwóch pól, więc bierze ją to o niższym
 * numerze kierunku — tak samo, jak przy liniach siatki.
 */
export function buildEdgeStones(
  members: HexCellData[],
  world: Map<number, HexCellData>,
  props: Props
): THREE.Group | null {
  const matrices: THREE.Matrix4[] = [];

  // Ile skalować model, żeby leżał **dokładnie wzdłuż krawędzi**. Bok heksa
  // foremnego równa się jego promieniowi, a model przychodzi znormalizowany do
  // wysokości jeden — `footprint` mówi więc, jak długi jest przy tej wysokości.
  const along = props.border.footprint || 1;
  const size = HEX_SIZE / along;

  for (const cell of members) {
    if (!habitable(cell)) continue;
    const { x, z } = axialToWorld(cell.q, cell.r);

    for (let edge = 0; edge < 3; edge += 1) {
      const across = neighbor(cell, edge);
      const there = world.get(axialKey(across.q, across.r));
      if (!there || !habitable(there)) continue;
      // Ta sama para funkcji, którą rządzi się ruch — nie ich kopia.
      if (passable(cell, there)) continue;

      const from = CORNERS[edge];
      const to = CORNERS[(edge + 1) % 6];
      const yFrom = elevationToY(cornerElevation(world, cell, edge));
      const yTo = elevationToY(cornerElevation(world, cell, (edge + 1) % 6));

      const alongX = to.x - from.x;
      const alongZ = to.z - from.z;
      const length = Math.hypot(alongX, alongZ) || 1;

      // Na zewnątrz, czyli w stronę sąsiada: środek krawędzi liczony od środka
      // pola wskazuje dokładnie tam. Znak zależy od numeru krawędzi, więc
      // kierunek „ku sąsiadowi" bierze się z geometrii, a nie ze zgadywania.
      const outX = (from.x + to.x) / 2;
      const outZ = (from.z + to.z) / 2;
      const reach = Math.hypot(outX, outZ) || 1;

      // Ku temu polu, które leży **niżej**: przegroda ma czytać się jak krawędź
      // urwiska, a nie jak płot na granicy działki. Gdy oba są równo — czego
      // reguła kroku i tak nie zatrzyma — nie ma dokąd jej zsuwać.
      const drop = Math.sign(there.elevation - cell.elevation) * TOWARDS_DROP;
      const leanX = (outX / reach) * drop;
      const leanZ = (outZ / reach) * drop;

      // **Wzdłuż krawędzi, a nie w poprzek.** Model jest najdłuższy w osi Z,
      // więc obrót wokół pionu ma przełożyć tę oś na kierunek krawędzi:
      // `atan2(x, z)`, bo obrót o θ przenosi (0,0,1) na (sin θ, 0, cos θ).
      const facing = Math.atan2(alongX, alongZ);
      // Krawędź bywa pochyła — jej końce leżą na różnych wysokościach. Przegroda
      // pochyla się razem z nią, bo próg ułożony poziomo na spadzistej granicy
      // wystaje z ziemi jednym końcem i tonie drugim.
      const pitch = Math.atan2(yTo - yFrom, length);

      matrices.push(
        new THREE.Matrix4().compose(
          new THREE.Vector3(
            x + outX + leanX,
            (yFrom + yTo) / 2 - size * SUNK,
            z + outZ + leanZ
          ),
          new THREE.Quaternion()
            .setFromAxisAngle(new THREE.Vector3(0, 1, 0), facing)
            .multiply(
              new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(1, 0, 0), -pitch)
            ),
          new THREE.Vector3(size, size, size)
        )
      );
    }
  }

  if (matrices.length === 0) return null;

  const group = new THREE.Group();
  group.name = 'edge-stones';

  // Jeden model, więc jedna instancja na część — bez grupowania po pliku,
  // którego tu już nie ma.
  for (const part of props.border.parts) {
    const mesh = new THREE.InstancedMesh(part.geometry, part.material, matrices.length);
    mesh.castShadow = true;
    mesh.receiveShadow = true;

    matrices.forEach((matrix, index) => mesh.setMatrixAt(index, matrix));
    mesh.instanceMatrix.needsUpdate = true;
    mesh.computeBoundingSphere();

    group.add(mesh);
  }

  return group;
}
